/**
 * Public Google Places client for the Book Shipment form.
 *
 * Proxies through the server so the Google key never reaches the browser:
 *   GET {BASE}/api/v1/location/public/autocomplete?input=…&sessionToken=…
 *   GET {BASE}/api/v1/location/public/details?placeId=…&sessionToken=…
 *
 * Both routes are IP-throttled server-side — they spend metered Places quota.
 */

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4000';

export type Prediction = {
  description: string;
  placeId: string;
};

export type PlaceDetails = {
  formattedAddress: string;
  latitude: number;
  longitude: number;
  city: string | null;
  state: string | null;
  country: string | null;
  countryCode: string | null;
  postalCode: string | null;
  placeId?: string;
};

/**
 * One UUID per "user starts typing → user picks one" cycle, so Google bills the
 * autocomplete calls and the follow-up details call as a single session rather
 * than per-keystroke.
 */
export const newSessionToken = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `s_${Math.random().toString(36).slice(2)}`;

async function get<T>(path: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${BASE}/api/v1/location/public/${path}`);
    if (!res.ok) return fallback;
    const json = await res.json();
    return (json?.data ?? fallback) as T;
  } catch {
    return fallback;
  }
}

export const autocomplete = (input: string, sessionToken: string) =>
  get<Prediction[]>(
    `autocomplete?input=${encodeURIComponent(input)}&sessionToken=${sessionToken}`,
    [],
  );

export const placeDetails = (placeId: string, sessionToken: string) =>
  get<PlaceDetails | null>(
    `details?placeId=${encodeURIComponent(placeId)}&sessionToken=${sessionToken}`,
    null,
  );
